import { useState, useCallback, useRef } from 'react';
import { toast, snackbar } from '@mobiscroll/react';
import { serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { addEvents, updateEvent, deleteEvent, restoreEvent } from '../../../Utils/firebaseCRUD';
import { addItem as addQueueItem } from '../../../Utils/offlineQueue';
// import { uuidToBase32Hex } from '../../../Utils/uuidToBase32';

// Custom hook for managing event popup state and logic
export const useEventPopup = ({ eventDatabase, myEvents, setMyEvents }) => {
    // Popup state
    const [isOpen, setOpen] = useState(false);
    const [isEdit, setEdit] = useState(false);
    const [anchor, setAnchor] = useState(null);
    
    // Form state
    const [popupEventTitle, setTitle] = useState('');
    const [popupEventDescription, setDescription] = useState('');
    const [popupEventAllDay, setAllDay] = useState(true);
    const [popupEventDate, setDate] = useState([]);
    const [popupEventTime, setTime] = useState([]);
    const [popupEventStatus, setStatus] = useState('busy');
    const [popupEventEdited, setEdited] = useState(null);
    const [popupEventCreated, setCreated] = useState(null);
    
    // Event properties
    const [isChecked, setIsChecked] = useState([]);
    const [isEditable, setIsEditable] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isX, setIsX] = useState(false);
    const [selectedColor, setSelectedColor] = useState('');
    const [tempColor, setTempColor] = useState('');
    
    // References
    const tempEvent = useRef(null);
    const originalResourcesRef = useRef(null); // eslint-disable-line no-unused-vars
    const [isManualSaving, setIsManualSaving] = useState(false);

    // Helper function to safely convert Firestore Timestamp or Date to Date object
    const getDateFromTimestamp = (timestamp) => {
        if (!timestamp) return null;
        
        if (timestamp instanceof Date) {
            return timestamp;
        }
        
        if (timestamp.seconds !== undefined) {
            return new Date(timestamp.seconds * 1000);
        }
        
        if (typeof timestamp === 'number') {
            return new Date(timestamp);
        }
        
        if (typeof timestamp === 'string') {
            const parsed = new Date(timestamp);
            return isNaN(parsed.getTime()) ? null : parsed;
        }
        
        return null;
    };

    // Helper function to count days between dates
    const countDaysTouched = (startDateStr, endDateStr) => {
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        const differenceInTime = endDate.getTime() - startDate.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

        return differenceInDays < 0 ? 0 : differenceInDays + 1;
    };

    // Helper function to check if time is midnight
    const isTimeMidnight = (dateString) => {
        const date = new Date(dateString);
        return date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
    };

    // Helper function to set time to current time
    const setTimeToNow = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        date.setHours(now.getHours());
        date.setMinutes(now.getMinutes());
        date.setSeconds(now.getSeconds());
        return date;
    };

    // Load popup form with event data
    const loadPopupForm = useCallback((event) => {
        setTitle(event.title);
        setDescription(event.description ? event.description : " ");
        
        if (isTimeMidnight(event.start) && isTimeMidnight(event.end)) {
            const updatedStart = setTimeToNow(event.start);
            const updatedEnd = setTimeToNow(event.end);
            setDate([updatedStart, updatedEnd]);
        } else {
            setDate([event.start, event.end]);
        }

        setAllDay(event.allDay ?? true);
        setStatus(event.status ?? 'busy');
        setCreated(event.createdAt || null);
        setEdited(event.updatedAt || null);
    }, []);

    // Form change handlers
    const titleChange = useCallback((ev) => {
        setTitle(ev.target.value);
    }, []);

    const descriptionChange = useCallback((ev) => {
        setDescription(ev.target.value);
    }, []);

    const allDayChange = useCallback((ev) => {
        setAllDay(ev.target.checked);
    }, []);

    const changeStartDate = useCallback((start, end) => {
        setDate([start.toDate(), end.toDate()]);
    }, []);

    const changeEndDate = useCallback((start, end) => {
        setDate([start.toDate(), end.toDate()]);
    }, []);

    const statusChange = useCallback((ev) => {
        setStatus(ev.target.value);
    }, []);

    // Event property change handlers
    const handleEventBusy = useCallback((ev) => {
        setIsBusy(ev.target.checked);
    }, []);

    const handleEventX = useCallback((ev) => {
        setIsX(ev.target.checked);
    }, []);

    const handleEventInvoiced = useCallback((ev) => {
        setIsEditable(ev.target.checked);
    }, []);

    const handleResourceCheckInEvent = useCallback((resource) => {
        setIsChecked((prevChecked) => {
            if (prevChecked.includes(resource.id)) {
                return prevChecked.filter((id) => id !== resource.id);
            } else {
                return [...prevChecked, resource.id];
            }
        });
    }, []);

    // Color handling
    const selectColor = useCallback((color) => {
        setTempColor(color);
    }, []);

    const changeColor = useCallback((ev) => {
        const color = ev.currentTarget.getAttribute('data-value');
        selectColor(color);
        setSelectedColor(color);
    }, [selectColor]);

    // Save event logic
    const saveEvent = useCallback(async () => {
        setIsManualSaving(true);
        
        const safetyTimeout = setTimeout(() => {
            setIsManualSaving(false);
        }, 5000);
        
        const updatedAtTimestamp = serverTimestamp();

        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

        // Helper to detect network errors that should trigger offline fallback in Electron (navigator.onLine can be unreliable)
        const isNetworkError = (err) => {
            const msg = String(err?.message || err || '').toLowerCase();
            return (
                msg.includes('unavailable') ||
                msg.includes('enetwork') ||
                msg.includes('enetunreach') ||
                msg.includes('ecconn') ||
                msg.includes('no connection established') ||
                msg.includes('network') ||
                msg.includes('failed to fetch')
            );
        };

        if (!isEdit && isOffline) {
            // Offline create: close immediately, queue notification, then attempt local write in background
            const confirmMsg = 'You are offline. This new event will be queued and synced when online. Proceed?';
            if (!window.confirm(confirmMsg)) { clearTimeout(safetyTimeout); setIsManualSaving(false); return; }

            const eventId = uuidv4();
            const createdAtTimestamp = serverTimestamp();

            // Close and notify immediately
            addQueueItem({
                type: 'create',
                entity: 'event',
                id: eventId,
                title: popupEventTitle,
                collection: eventDatabase,
                status: 'queued',
                after: {
                    id: eventId,
                    title: popupEventTitle,
                    description: popupEventDescription,
                    start: popupEventDate?.[0] || new Date(),
                    end: popupEventDate?.[1] || new Date(new Date().getTime() + 60 * 60 * 1000),
                    allDay: popupEventAllDay,
                    status: popupEventStatus,
                    color: selectedColor,
                    resource: isChecked,
                    editable: !isEditable,
                    busy: isBusy,
                    createdAt: createdAtTimestamp,
                }
            });
            toast({ message: 'Event saved offline. Will sync when back online.', color: 'warning' });
            setOpen(false);
            setEdit(false);
            tempEvent.current = null;
            setAnchor(null);
            setIsManualSaving(false);

            // Best-effort background local write to Firestore (offline persistence)
            try {
                const { collection, addDoc } = await import('firebase/firestore');
                const { database } = await import('../../../Utils/firebaseConfig');
                await addDoc(collection(database, eventDatabase), {
                    id: eventId,
                    title: popupEventTitle,
                    description: popupEventDescription,
                    start: popupEventDate?.[0] || new Date(),
                    end: popupEventDate?.[1] || new Date(new Date().getTime() + 60 * 60 * 1000),
                    allDay: popupEventAllDay,
                    status: popupEventStatus,
                    color: selectedColor,
                    resource: isChecked,
                    editable: !isEditable,
                    busy: isBusy,
                    createdAt: createdAtTimestamp,
                });
            } catch (error) {
                console.error('Offline create (background) failed:', error);
            } finally {
                clearTimeout(safetyTimeout);
            }
            return;
        }

        if (isEdit) {
            // Editing existing event
            const eventId = tempEvent.current?.id;
            if (!eventId) return;

            if (isOffline) {
                const confirmMsg = 'You are offline. This change will be queued and synced when online. Proceed?';
                if (!window.confirm(confirmMsg)) { clearTimeout(safetyTimeout); setIsManualSaving(false); return; }

                // Close and queue immediately
                addQueueItem({
                    type: 'update',
                    entity: 'event',
                    id: eventId,
                    title: popupEventTitle,
                    collection: eventDatabase,
                    status: 'queued',
                    before: { ...(tempEvent.current || {}) },
                    after: {
                        id: eventId,
                        title: popupEventTitle,
                        description: popupEventDescription,
                        start: popupEventDate?.[0] || new Date(),
                        end: popupEventDate?.[1] || new Date(new Date().getTime() + 60 * 60 * 1000),
                        allDay: popupEventAllDay,
                        status: popupEventStatus,
                        color: selectedColor,
                        resource: isChecked,
                        editable: !isEditable,
                        busy: isBusy,
                        updatedAt: updatedAtTimestamp
                    }
                });
                toast({ message: 'Changes saved offline. Will sync when back online.', color: 'warning' });
                setOpen(false);
                setEdit(false);
                tempEvent.current = null;
                setAnchor(null);
                setIsManualSaving(false);

                // Best-effort background local write
                try {
                    const { collection, query, where, getDocs, doc, updateDoc } = await import('firebase/firestore');
                    const { database } = await import('../../../Utils/firebaseConfig');
                    const q = query(collection(database, eventDatabase), where('id', '==', eventId));
                    const snap = await getDocs(q);
                    if (!snap.empty) {
                        const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                        await updateDoc(targetRef, {
                            title: popupEventTitle,
                            description: popupEventDescription,
                            start: popupEventDate?.[0] || new Date(),
                            end: popupEventDate?.[1] || new Date(new Date().getTime() + 60 * 60 * 1000),
                            allDay: popupEventAllDay,
                            status: popupEventStatus,
                            color: selectedColor,
                            resource: isChecked,
                            editable: !isEditable,
                            busy: isBusy,
                            updatedAt: updatedAtTimestamp
                        });
                    }
                } catch (e) {
                    console.error('Offline edit (background) failed:', e);
                } finally {
                    clearTimeout(safetyTimeout);
                }
                return;
            }

            updateEvent(
                eventDatabase,
                eventId,
                {
                    title: popupEventTitle,
                    description: popupEventDescription,
                    start: popupEventDate[0],
                    end: popupEventDate[1],
                    allDay: popupEventAllDay,
                    status: popupEventStatus,
                    color: selectedColor,
                    resource: isChecked,
                    editable: !isEditable,
                    busy: isBusy,
                    updatedAt: updatedAtTimestamp
                }
            ).then(() => {
                clearTimeout(safetyTimeout);
                toast({ message: 'Successfully edited event' });
                setOpen(false);
                setIsManualSaving(false);

                // Offer undo for edit similar to deletion flow
                const before = { ...(tempEvent.current || {}) };
                const afterData = {
                    id: eventId,
                    title: popupEventTitle,
                    description: popupEventDescription,
                    start: popupEventDate[0],
                    end: popupEventDate[1],
                    allDay: popupEventAllDay,
                    status: popupEventStatus,
                    color: selectedColor,
                    resource: isChecked,
                    editable: !isEditable,
                    busy: isBusy
                };
                setTimeout(() => {
                    snackbar({
                        button: {
                            action: async () => {
                                try {
                                    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
                                    if (offline) {
                                        try {
                                            const { collection, query, where, getDocs, doc, updateDoc } = await import('firebase/firestore');
                                            const { database } = await import('../../../Utils/firebaseConfig');
                                            const q = query(collection(database, eventDatabase), where('id', '==', eventId));
                                            const snap = await getDocs(q);
                                            if (!snap.empty) {
                                                const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                                                await updateDoc(targetRef, { ...before });
                                            }
                                            addQueueItem({
                                                type: 'update',
                                                entity: 'event',
                                                id: eventId,
                                                title: popupEventTitle,
                                                collection: eventDatabase,
                                                status: 'queued',
                                                before: { ...afterData },
                                                after: { id: eventId, ...before }
                                            });
                                            toast({ message: 'Changes reverted (offline). Will sync when online.', color: 'warning' });
                                            return;
                                        } catch (localErr) {
                                            console.error('Offline revert (background) failed:', localErr);
                                            toast({ message: 'Error reverting change (offline)' });
                                            return;
                                        }
                                    }

                                    const { serverTimestamp } = await import('firebase/firestore');
                                    await updateEvent(eventDatabase, eventId, { ...before, updatedAt: serverTimestamp() });
                                    toast({ message: 'Changes reverted' });
                                } catch (error) {
                                    const msg = String(error?.message || error || '').toLowerCase();
                                    const likelyNetwork = (
                                        msg.includes('unavailable') ||
                                        msg.includes('enetwork') ||
                                        msg.includes('enetunreach') ||
                                        msg.includes('ecconn') ||
                                        msg.includes('no connection established') ||
                                        msg.includes('network') ||
                                        msg.includes('failed to fetch')
                                    );
                                    if (likelyNetwork) {
                                        try {
                                            const { collection, query, where, getDocs, doc, updateDoc } = await import('firebase/firestore');
                                            const { database } = await import('../../../Utils/firebaseConfig');
                                            const q = query(collection(database, eventDatabase), where('id', '==', eventId));
                                            const snap = await getDocs(q);
                                            if (!snap.empty) {
                                                const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                                                await updateDoc(targetRef, { ...before });
                                            }
                                            addQueueItem({
                                                type: 'update',
                                                entity: 'event',
                                                id: eventId,
                                                title: popupEventTitle,
                                                collection: eventDatabase,
                                                status: 'queued',
                                                before: { ...afterData },
                                                after: { id: eventId, ...before }
                                            });
                                            toast({ message: 'Changes reverted (offline). Will sync when online.', color: 'warning' });
                                            return;
                                        } catch (fallbackErr) {
                                            console.error('Offline fallback revert failed:', fallbackErr);
                                        }
                                    }
                                    console.error('Error reverting change:', error);
                                    toast({ message: 'Error reverting change' });
                                }
                            },
                            text: 'Undo'
                        },
                        message: 'Event updated'
                    });
                }, 100);
            }).catch(async (error) => {
                // In Electron offline, fallback to renderer Firestore edit if network error
                if (isNetworkError(error)) {
                    try {
                        const { collection, query, where, getDocs, doc, updateDoc } = await import('firebase/firestore');
                        const { database } = await import('../../../Utils/firebaseConfig');
                        const q = query(collection(database, eventDatabase), where('id', '==', eventId));
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                            await updateDoc(targetRef, {
                                title: popupEventTitle,
                                description: popupEventDescription,
                                start: popupEventDate[0],
                                end: popupEventDate[1],
                                allDay: popupEventAllDay,
                                status: popupEventStatus,
                                color: selectedColor,
                                resource: isChecked,
                                editable: !isEditable,
                                busy: isBusy,
                                updatedAt: updatedAtTimestamp
                            });
                            clearTimeout(safetyTimeout);
                            addQueueItem({
                                type: 'update',
                                entity: 'event',
                                id: eventId,
                                title: popupEventTitle,
                                collection: eventDatabase,
                                status: 'queued',
                                before: { ...(tempEvent.current || {}) },
                                after: {
                                    id: eventId,
                                    title: popupEventTitle,
                                    description: popupEventDescription,
                                    start: popupEventDate[0],
                                    end: popupEventDate[1],
                                    allDay: popupEventAllDay,
                                    status: popupEventStatus,
                                    color: selectedColor,
                                    resource: isChecked,
                                    editable: !isEditable,
                                    busy: isBusy,
                                    updatedAt: updatedAtTimestamp
                                }
                            });
                            toast({ message: 'Changes saved offline. Will sync when back online.', color: 'warning' });
                            setOpen(false);
                            setEdit(false);
                            tempEvent.current = null;
                            setAnchor(null);
                            setIsManualSaving(false);
                            return;
                        }
                    } catch (fallbackErr) {
                        console.error('Offline fallback edit failed:', fallbackErr);
                    }
                }
                clearTimeout(safetyTimeout);
                console.error('Error editing event:', error);
                toast({ message: 'Error editing event' });
                setIsManualSaving(false);
            });

        } else {
            // Creating new event
            const eventId = uuidv4();
            const createdAtTimestamp = serverTimestamp();

            addEvents(
                eventDatabase,
                eventId,
                popupEventTitle,
                popupEventDescription,
                popupEventDate[0],
                popupEventDate[1],
                popupEventAllDay,
                popupEventStatus,
                selectedColor,
                isChecked,
                !isEditable,
                isBusy,
                createdAtTimestamp
            ).then(() => {
                clearTimeout(safetyTimeout);
                toast({ message: 'Successfully created event' });
                setOpen(false);
                setIsManualSaving(false);
            }).catch(async (error) => {
                // In Electron offline, fallback to renderer Firestore create if network error
                if (isNetworkError(error)) {
                    try {
                        const { collection, addDoc } = await import('firebase/firestore');
                        const { database } = await import('../../../Utils/firebaseConfig');
                        await addDoc(collection(database, eventDatabase), {
                            id: eventId,
                            title: popupEventTitle,
                            description: popupEventDescription,
                            start: popupEventDate[0],
                            end: popupEventDate[1],
                            allDay: popupEventAllDay,
                            status: popupEventStatus,
                            color: selectedColor,
                            resource: isChecked,
                            editable: !isEditable,
                            busy: isBusy,
                            createdAt: createdAtTimestamp,
                        });
                        addQueueItem({
                            type: 'create',
                            entity: 'event',
                            id: eventId,
                            title: popupEventTitle,
                            collection: eventDatabase,
                            status: 'queued',
                            after: {
                                id: eventId,
                                title: popupEventTitle,
                                description: popupEventDescription,
                                start: popupEventDate[0],
                                end: popupEventDate[1],
                                allDay: popupEventAllDay,
                                status: popupEventStatus,
                                color: selectedColor,
                                resource: isChecked,
                                editable: !isEditable,
                                busy: isBusy,
                                createdAt: createdAtTimestamp,
                            }
                        });
                        toast({ message: 'Event saved offline. Will sync when back online.', color: 'warning' });
                        clearTimeout(safetyTimeout);
                        setOpen(false);
                        setEdit(false);
                        tempEvent.current = null;
                        setAnchor(null);
                        setIsManualSaving(false);
                        return;
                    } catch (fallbackErr) {
                        console.error('Offline fallback create failed:', fallbackErr);
                    }
                }
                clearTimeout(safetyTimeout);
                console.error('Error creating event:', error);
                toast({ message: 'Error creating event' });
                setIsManualSaving(false);
            });
        }
    }, [
        eventDatabase,
        isEdit,
        popupEventTitle,
        popupEventDescription,
        popupEventDate,
        popupEventAllDay,
        popupEventStatus,
        selectedColor,
        isChecked,
        isEditable,
        isBusy
    ]);

    // Delete event logic
    const deleteLocalEvent = useCallback(async (event) => {
        console.log('deleteLocalEvent called with event:', event);
        console.log('Event ID:', event?.id);
        console.log('Event title:', event?.title);
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        const isNetworkError = (err) => {
            const msg = String(err?.message || err || '').toLowerCase();
            return (
                msg.includes('unavailable') ||
                msg.includes('enetwork') ||
                msg.includes('enetunreach') ||
                msg.includes('ecconn') ||
                msg.includes('no connection established') ||
                msg.includes('network') ||
                msg.includes('failed to fetch')
            );
        };

        if (isOffline) {
            const confirmMsg = 'You are offline. This deletion will be queued and synced when online. Proceed?';
            if (!window.confirm(confirmMsg)) { return; }

            // Close and queue immediately
            setOpen(false);
            setEdit(false);
            tempEvent.current = null;
            setAnchor(null);
            addQueueItem({
                type: 'delete',
                entity: 'event',
                id: event.id,
                title: event.title,
                collection: eventDatabase,
                status: 'queued',
                before: { ...(event || {}) }
            });
            toast({ message: 'Event deleted (offline). Will sync when online.', color: 'warning' });

            // Background local delete in renderer Firestore
            try {
                const { collection, query, where, getDocs, doc, deleteDoc } = await import('firebase/firestore');
                const { database } = await import('../../../Utils/firebaseConfig');
                const q = query(collection(database, eventDatabase), where('id', '==', event.id));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                    await deleteDoc(targetRef);
                }
            } catch (err) {
                console.error('Offline delete (background) failed:', err);
            }
            return;
        }

        deleteEvent(eventDatabase, event.id).then(() => {
            console.log('event deleted');
            // Close the popup after successful deletion
            setOpen(false);
            setEdit(false);
            tempEvent.current = null;
            setAnchor(null);
            // Show success message
            toast({ message: 'Event successfully deleted!' });
            // Show undo snackbar
            setTimeout(() => {
                snackbar({
                    button: {
                        action: async () => {
                            try {
                                // If offline, perform local add and queue for sync
                                const offline = typeof navigator !== 'undefined' && !navigator.onLine;
                                if (offline) {
                                    try {
                                        const { collection, addDoc } = await import('firebase/firestore');
                                        const { database } = await import('../../../Utils/firebaseConfig');
                                        const sanitized = Object.fromEntries(Object.entries(event || {}).filter(([_, v]) => v !== undefined));
                                        await addDoc(collection(database, eventDatabase), sanitized);
                                        addQueueItem({
                                            type: 'create',
                                            entity: 'event',
                                            id: event?.id,
                                            title: event?.title,
                                            collection: eventDatabase,
                                            status: 'queued',
                                            after: sanitized
                                        });
                                        toast({ message: 'Event restored (offline). Will sync when online.', color: 'warning' });
                                    } catch (localErr) {
                                        console.error('Offline restore (background) failed:', localErr);
                                        toast({ message: 'Error restoring event (offline)' });
                                    }
                                    return;
                                }

                                // Try online restore first
                                await restoreEvent(eventDatabase, event);
                                toast({ message: 'Event successfully restored' });
                            } catch (error) {
                                // Fallback to local add if network-related
                                const msg = String(error?.message || error || '').toLowerCase();
                                const likelyNetwork = (
                                    msg.includes('unavailable') ||
                                    msg.includes('enetwork') ||
                                    msg.includes('enetunreach') ||
                                    msg.includes('ecconn') ||
                                    msg.includes('no connection established') ||
                                    msg.includes('network') ||
                                    msg.includes('failed to fetch')
                                );
                                if (likelyNetwork) {
                                    try {
                                        const { collection, addDoc } = await import('firebase/firestore');
                                        const { database } = await import('../../../Utils/firebaseConfig');
                                        const sanitized = Object.fromEntries(Object.entries(event || {}).filter(([_, v]) => v !== undefined));
                                        await addDoc(collection(database, eventDatabase), sanitized);
                                        addQueueItem({
                                            type: 'create',
                                            entity: 'event',
                                            id: event?.id,
                                            title: event?.title,
                                            collection: eventDatabase,
                                            status: 'queued',
                                            after: sanitized
                                        });
                                        toast({ message: 'Event restored (offline). Will sync when online.', color: 'warning' });
                                        return;
                                    } catch (fallbackErr) {
                                        console.error('Offline fallback restore failed:', fallbackErr);
                                    }
                                }
                                console.error('Error restoring event:', error);
                                toast({ message: 'Error restoring event' });
                            }
                        },
                        text: 'Undo'
                    },
                    message: 'Event deleted'
                });
            }, 100);
        }).catch(async (error) => {
            // Fallback to offline behavior if network error while we thought we were online
            if (isNetworkError(error)) {
                // Close and queue immediately
                setOpen(false);
                setEdit(false);
                tempEvent.current = null;
                setAnchor(null);
                addQueueItem({
                    type: 'delete',
                    entity: 'event',
                    id: event.id,
                    title: event.title,
                    collection: eventDatabase,
                    status: 'queued',
                    before: { ...(event || {}) }
                });
                toast({ message: 'Event deleted (offline). Will sync when online.', color: 'warning' });
                // Background local delete
                try {
                    const { collection, query, where, getDocs, doc, deleteDoc } = await import('firebase/firestore');
                    const { database } = await import('../../../Utils/firebaseConfig');
                    const q = query(collection(database, eventDatabase), where('id', '==', event.id));
                    const snap = await getDocs(q);
                    if (!snap.empty) {
                        const targetRef = doc(database, eventDatabase, snap.docs[0].id);
                        await deleteDoc(targetRef);
                    }
                } catch (fallbackErr) {
                    console.error('Offline fallback delete failed:', fallbackErr);
                }
                return;
            }
            console.error('Error deleting event:', error);
            toast({ message: 'Error deleting event' });
        });
    }, [eventDatabase, setOpen, setEdit, setAnchor]);

    // Open popup for editing
    const openEditPopup = useCallback((event, anchorElement) => {
        console.log('openEditPopup called with event:', event);
        setEdit(true);
        tempEvent.current = { ...event };
        console.log('tempEvent.current set to:', tempEvent.current);
        setIsEditable(!event.editable);
        setSelectedColor(event.color || '#ff0000'); // Default color if none
        setIsBusy(event.busy);
        setIsChecked(event.resource || []);
        
        loadPopupForm(event);
        setAnchor(anchorElement);
        setOpen(true);
    }, [loadPopupForm]);

    // Open popup for creating new event
    const openCreatePopup = useCallback((event, anchorElement) => {
        if (isManualSaving) return;
        
        setEdit(false);
        tempEvent.current = event;
        setIsEditable(false);
        setSelectedColor('#ff0000'); // Default busy color
        setIsBusy(false);
        setIsChecked([event.resource]);
        
        loadPopupForm(event);
        setAnchor(anchorElement);
        setOpen(true);
    }, [loadPopupForm, isManualSaving]);

    // Close popup
    const closePopup = useCallback(() => {
        setOpen(false);
        setEdit(false);
        tempEvent.current = null;
        setAnchor(null);
    }, []);

    // Reset form state
    const resetForm = useCallback(() => {
        setTitle('');
        setDescription('');
        setDate([]);
        setTime([]);
        setAllDay(true);
        setStatus('busy');
        setCreated(null);
        setEdited(null);
        setIsChecked([]);
        setIsEditable(false);
        setIsBusy(false);
        setIsX(false);
        setSelectedColor('#ff0000');
        setTempColor('');
    }, []);

    return {
        // State
        isOpen,
        isEdit,
        anchor,
        popupEventTitle,
        popupEventDescription,
        popupEventAllDay,
        popupEventDate,
        popupEventTime,
        popupEventStatus,
        popupEventEdited,
        popupEventCreated,
        isChecked,
        isEditable,
        isBusy,
        isX,
        selectedColor,
        tempColor,
        isManualSaving,
        
        // References
        tempEvent,
        
        // Actions
        setOpen,
        setEdit,
        setAnchor,
        setTitle,
        setDescription,
        setAllDay,
        setDate,
        setTime,
        setStatus,
        setCreated,
        setEdited,
        setIsChecked,
        setIsEditable,
        setIsBusy,
        setIsX,
        setSelectedColor,
        setTempColor,
        
        // Event handlers
        titleChange,
        descriptionChange,
        allDayChange,
        changeStartDate,
        changeEndDate,
        statusChange,
        handleEventBusy,
        handleEventX,
        handleEventInvoiced,
        handleResourceCheckInEvent,
        selectColor,
        changeColor,
        
        // Main operations
        saveEvent,
        deleteLocalEvent,
        openEditPopup,
        openCreatePopup,
        closePopup,
        resetForm,
        loadPopupForm,
        
        // Helpers
        getDateFromTimestamp,
        countDaysTouched,
        isTimeMidnight,
        setTimeToNow
    };
};
